/* eslint-disable no-magic-numbers */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';

import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { datasetComparisonRef } from '../../../constants';
import { useReportBuilderContext } from '../../../report-builder.context';
import { DataField } from '../../../types';
import { useI18nConstants } from '../../../use-i18n-constants';
import { DatasetComparisonSettings } from '../dataset-comparison-settings';

import styles from './table-columns-selector.scss';

export const TableColumnsSelector = () => {
  const intl = useIntl();
  const {
    columnCalculations,
    getSelectedDatasets,
    getSelectedVarianceFields,
    addSelectedVarianceField,
    removeSelectedVarianceField
  } = useReportBuilderContext();
  const { getTransformedColumnFormulas } = useI18nConstants();
  const [calculations, setCalculations] = useState<DataField[]>([]);
  const selectedDatasets = getSelectedDatasets();
  const selectedVarianceFields = getSelectedVarianceFields();

  useEffect(() => {
    let calcs = getTransformedColumnFormulas(columnCalculations);
    if (selectedDatasets.length > 1) {
      calcs = calcs
        .filter(c => !c.disabledIfMultiDS)
        .map(c => ({ ...c, children: c.children?.filter(cc => !cc.disabledIfMultiDS) }));
    }
    if (selectedDatasets.length < 2) {
      calcs = calcs
        .filter(c => !c.disabledIfOneDS)
        .map(chil => ({
          ...chil,
          children: chil.children
            ?.filter(c => !c.disabledIfOneDS)
            .map(c => ({ ...c, children: c.children?.filter(cc => !cc.disabledIfOneDS) }))
        }));
    }
    setCalculations(calcs);
  }, [columnCalculations, getTransformedColumnFormulas, selectedDatasets.length]);

  const handleColumnCheckChange = (variance: DataField) => {
    const foundField = selectedVarianceFields.find(v => v.ref === variance.ref);
    if (foundField) {
      removeSelectedVarianceField(variance.ref);
    } else {
      addSelectedVarianceField(variance);
    }
  };

  return (
    <div className={clsx(styles.columnsSelector)}>
      <ul>
        {calculations.map((calc, index) => {
          return (
            <li key={index}>
              <div className={clsx(styles.calculation)}>
                <h4>{calc.name}</h4>
              </div>
              {calc.children && (
                <ul>
                  {calc.children.map((child, childIndex) => {
                    return child.children?.length || child.ref !== datasetComparisonRef ? (
                      <li key={childIndex}>
                        <div className={clsx(child.children?.length ? styles.subTitle : styles.calculation)}>
                          {child.children?.length ? null : (
                            <Checkbox
                              checked={!!selectedVarianceFields.find(v => v.ref === child.ref)}
                              onChange={() => handleColumnCheckChange(child)}
                              aria-label={intl.formatMessage(
                                { id: 'predict.toggle_element.aria_label', defaultMessage: `Toggle {name}` },
                                { name: child.name }
                              )}
                              label={child.name}
                              mb={1}
                            />
                          )}
                        </div>
                        {child.children?.length ? (
                          <ul className={styles.subList}>
                            {child.children.map((subChild, subChildIndex) => {
                              return (
                                <li key={subChildIndex}>
                                  <div className={clsx(styles.calculation)}>
                                    <Checkbox
                                      checked={!!selectedVarianceFields.find(v => v.ref === subChild.ref)}
                                      onChange={() => handleColumnCheckChange(subChild)}
                                      aria-label={intl.formatMessage(
                                        { id: 'predict.toggle_element.aria_label', defaultMessage: `Toggle {name}` },
                                        { name: subChild.name }
                                      )}
                                      label={subChild.name}
                                      mb={1}
                                    />
                                  </div>
                                  {child.ref === datasetComparisonRef &&
                                    selectedVarianceFields.find(v => v.ref === subChild.ref) && (
                                      <DatasetComparisonSettings field={subChild} />
                                    )}
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
